export function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: `application/${type}`,
    },
  };
}

export function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

class CartNote extends window.HTMLElement {
  constructor() {
    super();

    this.addEventListener('change', (event) => {
      event.stopPropagation();
      const body = JSON.stringify({ note: event.target.value });
      fetch(`${window.routes.cart_update_url}`, {
        ...fetchConfig(),
        ...{ body },
      });
    });
  }
}

window.customElements.define('cart-note', CartNote);

class CartRemoveButton extends window.HTMLElement {
  constructor() {
    super()
    this.addEventListener('click', (event) => {
      event.preventDefault()
      const cartItems =
        this.closest('cart-items') || this.closest('cart-drawer-items')
      cartItems.updateQuantity(this.dataset.index, 0)
    })
  }
}

window.customElements.define('cart-remove-button', CartRemoveButton)

class QuantityInput extends window.HTMLElement {
  constructor() {
    super()
    this.input = this.querySelector('input')
    this.changeEvent = new Event('change', { bubbles: true })

    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    )
  }

  onButtonClick(event) {
    event.preventDefault()
    const previousValue = this.input.value

    event.target.name === 'plus' ? this.input.stepUp() : this.input.stepDown()
    if (previousValue !== this.input.value)
      this.input.dispatchEvent(this.changeEvent)
  }
}

window.customElements.define('quantity-input', QuantityInput)

export default class CartItems extends window.HTMLElement {
  constructor() {
    super();

    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status');
    console.log("Cart Items initialized!!");
    console.log(this.lineItemStatusElement);

    this.currentItemCount = Array.from(
      this.querySelectorAll('[name="updates[]"]')
    ).reduce((total, quantityInput) => total + parseInt(quantityInput.value, 10), 0);

    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);

    this.addEventListener('change', this.debouncedOnChange);
  }

  onChange(event) {
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute('name')
    );
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items')?.dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  addtocart(productId, quantity) {
    this.enableLoading(productId);

    const body = JSON.stringify({
      id: productId,
      quantity: quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${window.routes.cart_add_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);
        this.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id)
              ?.querySelector(section.selector) ||
            document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });

        this.updateLiveRegions(productId, parsedState.item_count);
        this.disableLoading();
      })
      .catch(() => {
        this.querySelectorAll('.loading-overlay').forEach((overlay) =>
          overlay.classList.remove('active')
        );
        const errors = document.getElementById('cart-errors');
        errors.textContent = window.cartStrings?.error || "An error occurred";
        this.disableLoading();
      });
  }

  // A Gift with Purchase product changes together with its linked gift lines in one request.
  getLinkedGiftUpdates(line, quantity) {
    const row = this.querySelector(`#CartItem-${line}`);
    if (row?.dataset.gwpRole !== 'parent') return null;

    const gifts = this.querySelectorAll(
      `.cart-item[data-gwp-role="gift"][data-gwp-uid="${CSS.escape(row.dataset.gwpUid)}"]`
    );
    if (gifts.length === 0) return null;

    const updates = { [row.dataset.key]: Number(quantity) };
    gifts.forEach((gift) => {
      updates[gift.dataset.key] = Number(quantity);
    });
    return updates;
  }

  updateQuantity(line, quantity, name) {
    this.enableLoading(line);

    const updates = this.getLinkedGiftUpdates(line, quantity);
    const sections = {
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    };
    const body = JSON.stringify(updates ? { updates, ...sections } : { line, quantity, ...sections });
    const url = updates ? window.routes.cart_update_url : window.routes.cart_change_url;

    fetch(`${url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);
        this.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id)
              ?.querySelector(section.selector) ||
            document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });

        this.updateLiveRegions(line, parsedState.item_count);
        this.disableLoading();
      })
      .catch(() => {
        this.querySelectorAll('.loading-overlay').forEach((overlay) =>
          overlay.classList.remove('active')
        );
        const errors = document.getElementById('cart-errors');
        errors.textContent = window.cartStrings?.error || "An error occurred";
        this.disableLoading();
      });
  }

  updateLiveRegions(line, itemCount) {
    if (this.currentItemCount === itemCount) {
      const lineItemError = document.getElementById(`Line-item-error-${line}`);
      const quantityElement = document.getElementById(`Quantity-${line}`);
      lineItemError.innerHTML = window.cartStrings?.quantityError?.replace(
        '[quantity]',
        quantityElement.value
      );
    }

    this.currentItemCount = itemCount;
    this.lineItemStatusElement.setAttribute('aria-hidden', true);
  }

  getSectionInnerHTML(html, selector) {
    return new window.DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector)?.innerHTML || '';
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items');
    mainCartItems.classList.add('loading');

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading-overlay`
    );

    [...cartItemElements].forEach((overlay) =>
      overlay.classList.add('active')
    );

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading() {
    const mainCartItems = document.getElementById('main-cart-items');
    mainCartItems.classList.remove('loading');
  }
}

window.customElements.define('cart-items', CartItems);